/**
 * Wires runtime context into Sentry:
 *  - route change breadcrumbs (every client-side navigation)
 *  - authenticated user identity (id + admin flag), cleared on sign out
 *
 * Rendered once inside RootComponent. Safe no-op when Sentry has no DSN.
 */
import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { recordRouteChange, setSentryUser } from "@/lib/sentry";

export function SentryContextBridge() {
  const location = useRouterState({ select: (s) => s.location });
  const prevRef = useRef<{ pathname: string; search: string; hash: string } | null>(null);

  // Route change breadcrumbs.
  useEffect(() => {
    const next = {
      pathname: location.pathname,
      search: typeof location.searchStr === "string" ? location.searchStr : "",
      hash: location.hash ?? "",
    };
    recordRouteChange(next, prevRef.current);
    prevRef.current = next;
  }, [location.pathname, location.searchStr, location.hash]);

  // User context — id only by default (no PII).
  useEffect(() => {
    let cancelled = false;

    async function applyUser(userId: string | null, email: string | null) {
      if (cancelled) return;
      if (!userId) {
        setSentryUser(null);
        return;
      }
      let isAdmin: boolean | null = null;
      try {
        const { data } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId);
        if (Array.isArray(data)) {
          isAdmin = data.some((r: { role?: string }) => r.role === "admin");
        }
      } catch {
        // Ignore — a missing role table shouldn't break Sentry context.
      }
      if (cancelled) return;
      setSentryUser({ id: userId, isAdmin, email });
    }

    supabase.auth.getSession().then(({ data }) => {
      const user = data.session?.user ?? null;
      void applyUser(user?.id ?? null, user?.email ?? null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ?? null;
      void applyUser(user?.id ?? null, user?.email ?? null);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return null;
}
